import {useMemo} from 'react'
import {issuePath, pullRequestPath, type Repository} from '@github-ui/paths'
import {GitHubReferenceCards} from '@github-ui/copilot-shared-ui/components/GitHubReferenceCards'
import type {ReferenceItem} from '@github-ui/copilot-shared-ui/components/github-references'

import type {APITaskArtifact} from '../../types/artifact'
import styles from './ArtifactList.module.css'

const PULL_REQUEST_TYPE = 'pull_request'
const ISSUE_TYPE = 'issue'

export function isSafeExternalUrl(value: string): boolean {
  let url: URL
  try {
    // eslint-disable-next-line no-restricted-syntax -- arbitrary external URL; must not resolve against window.location.origin
    url = new URL(value)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
  if (url.username || url.password) return false
  if (!url.host) return false
  return true
}

function repositoryFromNwo(nwo: string | undefined): Repository | undefined {
  if (!nwo) return undefined
  const [ownerLogin, name, ...extra] = nwo.split('/')
  if (!ownerLogin || !name || extra.length > 0) return undefined
  return {ownerLogin, name}
}

function githubResourceHref(repository: Repository, type: string, id: number): string {
  return type === PULL_REQUEST_TYPE
    ? pullRequestPath({repo: repository, number: id})
    : issuePath({owner: repository.ownerLogin, repo: repository.name, issueNumber: id})
}

export function taskArtifactsToReferences(
  artifacts: APITaskArtifact[] | undefined,
  nwo: string | undefined,
): ReferenceItem[] {
  if (!artifacts || artifacts.length === 0) return []
  const repository = repositoryFromNwo(nwo)
  const references: ReferenceItem[] = []

  for (const artifact of artifacts) {
    if (artifact.provider === 'github' && artifact.type === 'branch') {
      // The working branch is task plumbing rather than a user-created output.
      continue
    }

    if (artifact.provider === 'github' && artifact.type === 'github_resource') {
      if (!repository || (artifact.data.type !== PULL_REQUEST_TYPE && artifact.data.type !== ISSUE_TYPE)) continue
      references.push({
        type: artifact.data.type === PULL_REQUEST_TYPE ? 'pr' : 'issue',
        url: githubResourceHref(repository, artifact.data.type, artifact.data.id),
        number: artifact.data.id,
        state: artifact.data.state,
        repo: `${repository.ownerLogin}/${repository.name}`,
      })
      continue
    }

    if (
      artifact.provider === 'azure_devops' &&
      artifact.type === 'azure_devops_resource' &&
      isSafeExternalUrl(artifact.data.url)
    ) {
      references.push({
        type: 'external',
        title: 'Azure DevOps resource',
        url: artifact.data.url,
        metadata: artifact.data.url,
      })
    }
  }

  return references
}

export interface ArtifactListProps {
  /** Raw task artifacts — see `APITaskArtifact` for the supported shapes. */
  artifacts?: APITaskArtifact[]
  /** `owner/repo` used to build GitHub-relative PR/issue links. GitHub-resource artifacts are skipped without it; the Azure DevOps artifact doesn't need it (its URL is self-contained). */
  nwo?: string
}

export function ArtifactList({artifacts, nwo}: ArtifactListProps) {
  const references = useMemo(() => taskArtifactsToReferences(artifacts, nwo), [artifacts, nwo])
  if (references.length === 0) return null

  return (
    <div className={styles.container}>
      <GitHubReferenceCards items={references} aria-label="Task artifacts" />
    </div>
  )
}
